import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { PageHeader } from '@/components/PageHeader';
import { PrimaryButton } from '@/components/PrimaryButton';
import {
  getBranchOptions,
  getPhoneModelOptions,
  getContactSettings,
  getInternalQuotation,
  registerQuotationContact,
  updateInternalQuotation,
} from '@/features/operations/api';
import { ConfirmationEmailStatus } from '@/features/operations/components/ConfirmationEmailStatus';
import { DeclaredStateDetail } from '@/features/operations/components/DeclaredStateDetail';
import { EstimateBreakdownDetail } from '@/features/operations/components/EstimateBreakdownDetail';
import { IntakeOutcomeAlert } from '@/features/operations/components/IntakeOutcomeAlert';
import { CounterAccountNotice } from '@/features/customers/components/CounterAccountNotice';
import { CorrectQuotationAccountDialog } from '@/features/customers/components/CorrectQuotationAccountDialog';
import { QuotationAccountSection } from '@/features/customers/components/QuotationAccountSection';
import { NotesTimeline } from '@/features/operations/components/NotesTimeline';
import { QuotationPhotoGallery } from '@/features/operations/components/QuotationPhotoGallery';
import { ReceptionPanel } from '@/features/operations/components/ReceptionPanel';
import { StatusHistoryTimeline } from '@/features/operations/components/StatusHistoryTimeline';
import {
  estimateUnavailableLabels,
  formatQuotationDate,
  originLabels,
} from '@/features/operations/labels';
import { toPriceInput } from '@/features/operations/money';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import type {
  BranchOption,
  DeviceModelOption,
  ContactSettings,
  DeviceDetail,
  QuotationRequestItem,
} from '@/features/operations/types';
import { useNotes } from '@/features/operations/useNotes';
import { buildWhatsAppUrl, isWhatsAppReachable } from '@/features/operations/whatsapp';
import { StatusChip } from '@/components/StatusChip';

/// The status is not part of the draft: it changes only through the reception (ADR 0005).
type QuotationDraft = {
  estimatedAmount: string;
};

function buildQuotationDraft(item: QuotationRequestItem): QuotationDraft {
  return {
    // Only a manually entered amount is editable here. An automatic one is shown above as
    // read-only, so an employee never edits a figure the rules produced without noticing.
    estimatedAmount:
      // With its cents: saving the form unchanged must not round the amount down.
      item.estimate && item.estimate.source === 'MANUAL'
        ? toPriceInput(item.estimate.amountMinor)
        : '',
  };
}

/// What the list or the counter intake hand over when they open a request.
type DetailLocationState = {
  /// The list's query string, so the way back returns to the same search.
  from?: string;
  /// Set by the counter intake: the outcome is what whoever is attending says out loud next.
  justCreated?: boolean;
  /// Set by the counter intake too: registering created the account and sent its invitation.
  invitationSent?: boolean;
} | null;

/**
 * One request, with room for both halves of WF-02's comparison: on the left what was declared
 * and how the request is being worked; on the right the phone PhoneTec received, and its
 * internal sheet (#88).
 */
export function QuotationDetailPage() {
  const { formatMoney } = usePriceDisplay();
  const { token } = useAuth();
  const { id = '' } = useParams();
  const location = useLocation();
  const locationState = location.state as DetailLocationState;
  const backTo = `/admin/quotations${locationState?.from ? `?${locationState.from}` : ''}`;
  const [quotation, setQuotation] = useState<QuotationRequestItem | null>(null);
  const [draft, setDraft] = useState<QuotationDraft | null>(null);
  const [modelOptions, setModelOptions] = useState<DeviceModelOption[]>([]);
  const [branchOptions, setBranchOptions] = useState<BranchOption[]>([]);
  const [contactSettings, setContactSettings] = useState<ContactSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isContacting, setIsContacting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isCorrectingAccount, setIsCorrectingAccount] = useState(false);
  const quotationNotes = useNotes(token, { type: 'quotation', id });

  useEffect(() => {
    const load = async () => {
      if (!token) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      try {
        // The contact message does not change per request; the models (with their colours) and
        // the branches are for reception, where the phone on the counter is checked and given a
        // place to stand.
        const [item, models, branches, contact] = await Promise.all([
          getInternalQuotation(token, id),
          getPhoneModelOptions(token),
          getBranchOptions(token),
          getContactSettings(token),
        ]);
        setQuotation(item);
        setDraft(buildQuotationDraft(item));
        setModelOptions(models);
        setBranchOptions(branches);
        setContactSettings(contact);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'No pudimos cargar la solicitud.');
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [token, id]);

  if (!token) {
    return null;
  }

  const backLink = (
    <Box>
      <Button component={RouterLink} to={backTo} color="inherit">
        ← Volver a cotizaciones
      </Button>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '30vh' }}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (!quotation || !draft) {
    return (
      <Stack spacing={3}>
        {backLink}
        <Alert severity="error">{errorMessage || 'No encontramos la solicitud.'}</Alert>
      </Stack>
    );
  }

  const handleQuotationSave = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const updatedQuotation = await updateInternalQuotation(token, quotation.id, {
        estimatedAmount: draft.estimatedAmount || undefined,
      });

      setQuotation(updatedQuotation);
      setDraft(buildQuotationDraft(updatedQuotation));
      setSuccessMessage(`Solicitud ${quotation.id} actualizada.`);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'No pudimos actualizar la solicitud.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Records the contact. Deliberately not awaited before WhatsApp opens: the anchor navigates
   * on its own, and blocking that on a round trip is what popup blockers kill.
   *
   * If recording fails the conversation still opened, so the error is surfaced without
   * pretending the contact did not happen -- it did, it just is not on file.
   */
  const handleWhatsAppContact = async () => {
    setIsContacting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const note = await registerQuotationContact(token, quotation.id, 'WHATSAPP');

      quotationNotes.append(note);
      setQuotation((current) =>
        current
          ? {
              ...current,
              lastContact: { contactedBy: note.author, createdAt: note.createdAt },
            }
          : current,
      );
      setSuccessMessage('Contacto registrado.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'No pudimos registrar el contacto.');
    } finally {
      setIsContacting(false);
    }
  };

  /// The request comes back carrying its device, which is now its internal sheet.
  const handleReceived = (receivedQuotation: QuotationRequestItem) => {
    setQuotation(receivedQuotation);
    setErrorMessage(null);
    // The status the request came back in says which decision closed the reception (#92).
    setSuccessMessage(
      receivedQuotation.status === 'REJECTED'
        ? `Cotización de ${receivedQuotation.contact.name} rechazada. Lo observado en la recepción quedó guardado para aprobarla si se renegocia.`
        : `Equipo de ${receivedQuotation.contact.name} recibido. La cotización quedó aprobada y el equipo, pendiente de publicación.`,
    );
  };

  /// The detail comes back with the new account and its trail (#108).
  const handleAccountChanged = (
    updated: QuotationRequestItem,
    previousInvitationRevoked: boolean,
  ) => {
    setIsCorrectingAccount(false);
    setQuotation(updated);
    setErrorMessage(null);
    setSuccessMessage(
      [
        updated.account
          ? `La cotización pasó a la cuenta de ${updated.account.fullName}. El contacto original no cambió.`
          : 'La cotización pasó a la otra cuenta.',
        // The previous account's invitation no longer works (#108). Said only when it had one.
        previousInvitationRevoked
          ? 'La invitación de la cuenta anterior dejó de funcionar; si hace falta, se reenvía desde su ficha.'
          : '',
      ]
        .filter(Boolean)
        .join(' '),
    );
  };

  /// Somebody corrected it first, or its account was activated meanwhile: show how it is now.
  const handleAccountConflict = async () => {
    setIsCorrectingAccount(false);
    setSuccessMessage(null);
    const reason =
      'No pudimos mover la cotización: mientras tanto otra persona la corrigió o la cuenta se activó.';
    setErrorMessage(`${reason} Te mostramos cómo quedó.`);

    try {
      setQuotation(await getInternalQuotation(token, quotation.id));
    } catch {
      setErrorMessage(`${reason} Recargá la página para ver cómo quedó.`);
    }
  };

  const handleDeviceUpdated = (device: DeviceDetail) => {
    setQuotation((current) => (current ? { ...current, device } : current));
    setErrorMessage(null);
    setSuccessMessage('Ficha del equipo actualizada.');
  };

  // Three separate reasons the action cannot run, told apart on purpose: an employee looking at
  // a dead button needs to know whether the problem is the request, the stored number or a
  // missing configuration. The number is the one this request was filed with (ADR 0004).
  const hasReachablePhone = isWhatsAppReachable(quotation.contact.phone);
  const hasReportedPhone =
    Boolean(quotation.contact.phone.trim()) && quotation.contact.phone !== 'No informado';
  // The settings may exist with only PhoneTec's public number in them; what this action needs is
  // the message.
  const hasMessage = Boolean(contactSettings?.whatsappMessageTemplate);
  const canContact = hasReachablePhone && hasMessage;
  const contactBlockedReason = !hasReportedPhone
    ? 'La solicitud no registró teléfono.'
    : !hasReachablePhone
      ? 'El teléfono guardado no está en formato internacional, así que no se puede abrir WhatsApp.'
      : !hasMessage
        ? 'Configurá el mensaje inicial en Administración → Cotización.'
        : null;

  return (
    <Stack spacing={3}>
      {backLink}
      <PageHeader
        eyebrow="SOLICITUD DE COTIZACIÓN"
        title={`${quotation.brand} ${quotation.model}`}
        description={`${quotation.contact.name} · ${
          quotation.storageGb === null ? '' : `${quotation.storageGb}GB declarados · `
        }Recibida el ${formatQuotationDate(quotation.createdAt)}`}
        badge={<StatusChip domain="quotation" status={quotation.status} size="medium" />}
      />

      {locationState?.justCreated ? <IntakeOutcomeAlert quotation={quotation} /> : null}
      {locationState?.justCreated ? (
        <CounterAccountNotice
          account={quotation.account}
          invitationSent={Boolean(locationState.invitationSent)}
        />
      ) : null}
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
      {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {/* Which channel it came through. Both are worked the same way; this only
                      says who typed it. */}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={originLabels[quotation.origin] || quotation.origin}
                  />
                  {quotation.lastContact ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="success"
                      label={`Contactado por ${
                        quotation.lastContact.contactedBy?.fullName || 'PhoneTec'
                      } · ${formatQuotationDate(quotation.lastContact.createdAt)}`}
                    />
                  ) : null}
                </Stack>
                {/* The account it belongs to today, which can be corrected (#108), apart from the
                    contact the request froze, which never changes (BR-016). */}
                <QuotationAccountSection
                  quotation={quotation}
                  onCorrect={() => setIsCorrectingAccount(true)}
                />
                <Stack spacing={0.5}>
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      CONTACTO ORIGINAL
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Como se registró en la solicitud. No cambia si se corrige la cuenta.
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {quotation.contact.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ overflowWrap: 'anywhere' }}
                  >
                    Email: {quotation.contact.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Teléfono: {quotation.contact.phone}
                  </Typography>
                  {/* Beside the contact it was sent to, which is where somebody looks when the
                      applicant says nothing arrived. */}
                  <ConfirmationEmailStatus confirmationEmail={quotation.confirmationEmail} />
                </Stack>
                <Box>
                  {canContact ? (
                    // A real anchor, not window.open inside an async handler: the click itself
                    // navigates, so no popup blocker can cut it, and recording the contact runs
                    // alongside instead of gating it.
                    <Button
                      variant="contained"
                      color="primary"
                      component="a"
                      href={buildWhatsAppUrl(
                        quotation.contact.phone,
                        contactSettings?.whatsappMessageTemplate || '',
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                      disabled={isContacting}
                      onClick={() => {
                        void handleWhatsAppContact();
                      }}
                    >
                      {isContacting ? 'Registrando…' : 'Contactar por WhatsApp'}
                    </Button>
                  ) : (
                    <PrimaryButton disabled>Contactar por WhatsApp</PrimaryButton>
                  )}
                  {contactBlockedReason ? (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {contactBlockedReason}
                    </Typography>
                  ) : null}
                </Box>
                <DeclaredStateDetail quotation={quotation} />
                {quotation.estimate && quotation.estimate.source === 'AUTOMATIC' ? (
                  <Box>
                    <Typography variant="overline" color="text.secondary">
                      ESTIMACIÓN AUTOMÁTICA
                    </Typography>
                    <Typography variant="h6">
                      {formatMoney(quotation.estimate.amountMinor, quotation.estimate.currency)}
                    </Typography>
                    {/* The frozen copy of the values that produced the number, read instead of
                        today's prices: it is what keeps this amount explainable after every
                        price on file has changed (BR-013). */}
                    <EstimateBreakdownDetail
                      breakdown={quotation.estimate.breakdown}
                      currency={quotation.estimate.currency}
                    />
                  </Box>
                ) : null}
                {!quotation.estimate && quotation.estimateUnavailableReason ? (
                  <Typography variant="body2" color="text.secondary">
                    Sin estimación automática:{' '}
                    {estimateUnavailableLabels[quotation.estimateUnavailableReason] ||
                      quotation.estimateUnavailableReason}
                  </Typography>
                ) : null}
                <TextField
                  fullWidth
                  label="Precio estimado manual (USD)"
                  value={draft.estimatedAmount}
                  placeholder="300"
                  helperText="Debe ser mayor que cero. Sobrescribe la estimación automática y queda marcado como manual."
                  onChange={(event) => setDraft({ ...draft, estimatedAmount: event.target.value })}
                />
                <Box>
                  <PrimaryButton onClick={handleQuotationSave} disabled={isSaving}>
                    {isSaving ? 'Guardando...' : 'Guardar solicitud'}
                  </PrimaryButton>
                </Box>
                <QuotationPhotoGallery
                  token={token}
                  quotationId={quotation.id}
                  photoCount={quotation.photos.length}
                />
                <StatusHistoryTimeline entries={quotation.statusHistory} />
                <NotesTimeline
                  notes={quotationNotes.notes}
                  isLoading={quotationNotes.isLoading}
                  error={quotationNotes.error}
                  onAdd={quotationNotes.add}
                  onEdit={quotationNotes.edit}
                />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <ReceptionPanel
                token={token}
                quotation={quotation}
                modelOptions={modelOptions}
                branchOptions={branchOptions}
                onReceived={handleReceived}
                onDeviceUpdated={handleDeviceUpdated}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {quotation.account ? (
        <CorrectQuotationAccountDialog
          token={token}
          open={isCorrectingAccount}
          quotation={quotation}
          currentAccount={quotation.account}
          onClose={() => setIsCorrectingAccount(false)}
          onChanged={handleAccountChanged}
          onConflict={() => void handleAccountConflict()}
        />
      ) : null}
    </Stack>
  );
}
